// Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Spinner,
  Table,
  SvgIcon,
  PageNavigationButtons,
  Warning,
  Modal,
  Heading
} from '../'

import {
  decodeBase64,
  PageNavigationControl,
  getPageCursor
} from '../../utils'

import { MessageTableRow } from './MessageTableRow'

const modalRef = React.createRef()


class WhatsAppMessages extends React.PureComponent {
  static propTypes = {
    status: PropTypes.string,
    direction: PropTypes.string,
    messageType: PropTypes.string,
    createdAtStartDate: PropTypes.string,
    createdAtEndDate: PropTypes.string,
    search: PropTypes.string,
    fromIdentityId: PropTypes.string,
    toIdentityId: PropTypes.string,
    orderBy: PropTypes.string,
    changeSort: PropTypes.func,
  }

  static defaultProps = {
    resultsPerPage: 15
  }

  state = {
    showUnparsedMessageModal: false,
    pageNav: new PageNavigationControl()
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      // click is outside, close
      this.setState({ showUnparsedMessageModal: false })
    }
  }

  goToDetailPage = (messageId, fromIdentityId, toIdentityId) => {
    if (fromIdentityId) {
      this.props.history.push(`/os/identity/${fromIdentityId}/communications/whatsapp/${messageId}`)
    } else if (toIdentityId) {
      this.props.history.push(`/os/identity/${toIdentityId}/communications/whatsapp/${messageId}`)
    } else {
      this.props.history.push(`/os/communications/whatsapp/${messageId}`)
    }
  }

  render() {
    if (this.props.loading) {
      return (
        <div className='slds-size_1-of-1 slds-is-relative'>
          <Spinner type='tail' />
        </div>
      )
    }

    if (this.props.error) {
      return (
        <Warning className='slds-m-top_x-large slds-m-left_medium'>
          Failed to fetch messages
        </Warning>
      )
    }

    const messages = this.props.whatsappmessages
    const pageNav = this.state.pageNav
    const { fromIdentityId, toIdentityId, fetchNext, fetchPrevious, refetch, resultsPerPage } = this.props

    if (messages.edges.length === 0) {
      return (
        <section className='slds-m-top_x-large slds-m-left_medium'>
          <SvgIcon
            variant='utility'
            symbol='info'
            size='small'
            colour='light'
            className='slds-m-right_x-small'
          />
          No matching messages found.
        </section>
      )
    } else {
      const startCursor = messages.pageInfo.startCursor
      const totalCount = messages.totalCount

      let columns = ['Identity', 'Direction', 'Status', 'Date/Time', 'Type', 'Message']
      if (this.props.actionColumn) {
        columns.push(this.props.actionColumn.name)
      }

      return (
        <React.Fragment>
          <Table
            columns={columns}
            rows={messages.edges.map(({ node: message }) => (
              <MessageTableRow
                message={message}
                messageIdentity={message.direction === 'INBOUND' && message.fromIdentity !== null
                  ? message.fromIdentity
                  : message.direction === 'OUTBOUND' && message.toIdentity !== null
                    ? message.toIdentity
                    : null
                }
                actionColumn={this.props.actionColumn}
                onClick={() => (
                  message.direction === "INBOUND" && message.status === 'RECEIVED'
                    ? this.setState({ showUnparsedMessageModal: true })
                    : this.goToDetailPage(
                        message.id,
                        fromIdentityId || (message.fromIdentity && message.fromIdentity.id),
                        toIdentityId || (message.toIdentity && message.toIdentity.id)
                      )
                )}
                key={`messageTableRow_${message.id}`}
              />
            ))}
            objectRows={true}
            orderBy={this.props.orderBy}
            changeSort={this.props.changeSort}
            querySortableColumns={{
              'Date/Time': {
                orderByName: 'created_at'
              }
            }}
            fixedLayout={true}
          />
          <PageNavigationButtons
            onNextClick={() => pageNav.nextPage(fetchNext)}
            onPreviousClick={() => pageNav.previousPage(fetchPrevious)}
            previousDisabled={pageNav.firstPage}
            nextDisabled={pageNav.lastPage}
            startCursor={startCursor}
            totalCount={totalCount}
            resultsPerPage={resultsPerPage}
            resultsText='messages'
            onPageNumberClick={pageNumber => refetch({ cursor: getPageCursor(pageNumber, resultsPerPage) })}
            className="slds-p-around_medium slds-float_right"
          />
          {this.state.showUnparsedMessageModal &&
            <Modal
              ref={modalRef}
              body={
                <div className='slds-align_absolute-center' style={{ display: 'block' }}>
                  <div className='slds-m-bottom_large slds-align_absolute-center'>
                    <SvgIcon
                      variant='utility'
                      symbol='ban'
                      size='large'
                      colour='warning'
                    />
                  </div>
                  <div>
                    <Heading className='slds-text-align_center'>
                      Details of the selected message cannot be shown since message has not been parsed yet.
                      <br />
                      <br />
                      Parsing should happen automatically within the next minute.
                    </Heading>
                  </div>
                </div>
              }
            />
          }
        </React.Fragment>
      )
    }
  }
}

const getMessages = gql`
  query($fromIdentityId: UUID, $toIdentityId: UUID, $fromEmployerId: UUID, $toEmployerId: UUID, $status: String, $direction: String, $messageType: String, $createdOnOrAfter: DateTime, $createdOnOrBefore: DateTime, $unknownSender: Boolean, $unknownReceiver: Boolean, $search: String, $orderBy: String!, $resultsPerPage: Int!, $cursor: String) {
    whatsappmessages(fromIdentity_Id: $fromIdentityId, toIdentity_Id: $toIdentityId, fromIdentity_Client_Division_Employer_Id: $fromEmployerId, toIdentity_Client_Division_Employer_Id: $toEmployerId, status: $status, direction: $direction, messageType: $messageType, createdOnOrAfter: $createdOnOrAfter, createdOnOrBefore: $createdOnOrBefore, fromIdentity_Id_Isnull: $unknownSender, toIdentity_Id_Isnull: $unknownReceiver, search: $search, orderBy: $orderBy, first: $resultsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          id
          body
          direction
          messageType
          status
          createdAt
          fromIdentity {
            id
            fullName
          }
          toIdentity {
            id
            fullName
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const getPreviousMessages = gql`
  query($fromIdentityId: UUID, $toIdentityId: UUID, $fromEmployerId: UUID, $toEmployerId: UUID, $status: String, $direction: String, $messageType: String, $createdOnOrAfter: DateTime, $createdOnOrBefore: DateTime, $unknownSender: Boolean, $unknownReceiver: Boolean, $search: String, $orderBy: String!, $resultsPerPage: Int!, $cursor: String) {
    whatsappmessages(fromIdentity_Id: $fromIdentityId, toIdentity_Id: $toIdentityId, fromIdentity_Client_Division_Employer_Id: $fromEmployerId, toIdentity_Client_Division_Employer_Id: $toEmployerId, status: $status, direction: $direction, messageType: $messageType, createdOnOrAfter: $createdOnOrAfter, createdOnOrBefore: $createdOnOrBefore, fromIdentity_Id_Isnull: $unknownSender, toIdentity_Id_Isnull: $unknownReceiver, search: $search, orderBy: $orderBy, last: $resultsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          id
          body
          direction
          messageType
          status
          createdAt
          fromIdentity {
            id
            fullName
          }
          toIdentity {
            id
            fullName
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const setUnknownParticipantQueryFilter = (unknownParticipant, direction, senderFilter) => {
  // 'directionCorrespondsToFilter', if applied to 'unknownSender' query filter, will be true if
  //    message direction is inbound
  // 'directionCorrespondsToFilter', if applied to 'unknownReceiver' query filter, will be true
  //    if message direction is outbound
  const directionCorrespondsToFilter = senderFilter
    ? direction === 'inbound'
    : direction === 'outbound'  // receiver

  return unknownParticipant && directionCorrespondsToFilter
   ? true
   : directionCorrespondsToFilter
     ? false
     : undefined
}

const withData = graphql(getMessages, {
  options: ({ fromIdentityId, toIdentityId, fromEmployerId, toEmployerId, status, direction, messageType, createdAtStartDate, createdAtEndDate, unknownParticipant, search, resultsPerPage = 15, orderBy }) => ({
    variables: {
      fromIdentityId: fromIdentityId ? decodeBase64(fromIdentityId) : null,
      toIdentityId: toIdentityId ? decodeBase64(toIdentityId) : null,
      fromEmployerId: fromEmployerId ? decodeBase64(fromEmployerId) : null,
      toEmployerId: toEmployerId ? decodeBase64(toEmployerId) : null,
      status,
      direction,
      messageType,
      createdOnOrAfter: createdAtStartDate,
      createdOnOrBefore: createdAtEndDate,
      unknownSender: setUnknownParticipantQueryFilter(unknownParticipant, direction, true),
      unknownReceiver: setUnknownParticipantQueryFilter(unknownParticipant, direction, false),
      search,
      resultsPerPage,
      orderBy
    },
    fetchPolicy: 'network-only'
  }),
  props: ({ ownProps: { fromIdentityId, toIdentityId, fromEmployerId, toEmployerId, status, direction, messageType, createdAtStartDate, createdAtEndDate, unknownParticipant, search, resultsPerPage = 15, orderBy }, data: { loading, error, refetch, whatsappmessages, fetchMore } }) => ({
    loading,
    error,
    refetch,
    whatsappmessages,
    fetchNext: () => {
      return fetchMore({
        query: getMessages,
        variables: {
          fromIdentityId: fromIdentityId ? decodeBase64(fromIdentityId) : null,
          toIdentityId: toIdentityId ? decodeBase64(toIdentityId) : null,
          fromEmployerId: fromEmployerId ? decodeBase64(fromEmployerId) : null,
          toEmployerId: toEmployerId ? decodeBase64(toEmployerId) : null,
          status,
          direction,
          messageType,
          createdOnOrAfter: createdAtStartDate,
          createdOnOrBefore: createdAtEndDate,
          unknownSender: setUnknownParticipantQueryFilter(unknownParticipant, direction, true),
          unknownReceiver: setUnknownParticipantQueryFilter(unknownParticipant, direction, false),
          search,
          resultsPerPage,
          orderBy,
          cursor: whatsappmessages.pageInfo.endCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.whatsappmessages.edges
          const pageInfo = fetchMoreResult.whatsappmessages.pageInfo
          return {
            whatsappmessages: {
              __typename: previousResult.whatsappmessages.__typename,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    },
    fetchPrevious: () => {
      return fetchMore({
        query: getPreviousMessages,
        variables: {
          fromIdentityId: fromIdentityId ? decodeBase64(fromIdentityId) : null,
          toIdentityId: toIdentityId ? decodeBase64(toIdentityId) : null,
          fromEmployerId: fromEmployerId ? decodeBase64(fromEmployerId) : null,
          toEmployerId: toEmployerId ? decodeBase64(toEmployerId) : null,
          status,
          direction,
          messageType,
          createdOnOrAfter: createdAtStartDate,
          createdOnOrBefore: createdAtEndDate,
          unknownSender: setUnknownParticipantQueryFilter(unknownParticipant, direction, true),
          unknownReceiver: setUnknownParticipantQueryFilter(unknownParticipant, direction, false),
          search,
          resultsPerPage,
          orderBy,
          cursor: whatsappmessages.pageInfo.startCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.whatsappmessages.edges
          const pageInfo = fetchMoreResult.whatsappmessages.pageInfo
          return {
            whatsappmessages: {
              __typename: previousResult.whatsappmessages.__typename,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    }
  })
})

export default compose(
  withData,
  withRouter
)(WhatsAppMessages)



// WEBPACK FOOTER //
// ./src/components/whatsapp-messages/index.js